import React, { useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft,
  PackageCheck,
  Calendar,
  Package,
  Scale,
  TrendingUp,
  Users,
  BarChart2,
  Loader2,
  Download,
  Clock,
  Sun,
  Sunset,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import * as XLSX from 'xlsx';
import { getAlistamientosByLavanderia } from './services/alistamientos.services';

// ── Constantes ────────────────────────────────────────────────────────────────
const ZONAS = ['Lenceria', 'Toallas'];
const ZONA_COLORS = {
  Lenceria: { bar: '#8b5cf6', badge: 'bg-purple-100 text-purple-800', border: 'border-l-purple-500', text: 'text-purple-600', bg: 'bg-purple-100' },
  Toallas:  { bar: '#3b82f6', badge: 'bg-blue-100 text-blue-800',   border: 'border-l-blue-400',  text: 'text-blue-600',   bg: 'bg-blue-100'   },
};
const TURNO_CONFIG = {
  Mañana: { icon: '🌅', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', bar: '#f59e0b' },
  Tarde:  { icon: '🌆', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', bar: '#f97316' },
  Otro:   { icon: '🌙', color: 'text-slate-500',  bg: 'bg-slate-50',  border: 'border-slate-200',  bar: '#94a3b8' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtNum = n => (n ?? 0).toLocaleString('es-CO');
const fmtKg  = n => `${(n ?? 0).toFixed(1)} kg`;
const toMin  = t => { const [h, m] = (t || '00:00').split(':').map(Number); return h * 60 + m; };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg text-sm">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((e, i) => (
        <p key={i} style={{ color: e.color }}>
          {e.name}:{' '}
          {typeof e.value === 'number'
            ? e.value % 1 === 0 ? fmtNum(e.value) : e.value.toFixed(2)
            : e.value}
        </p>
      ))}
    </div>
  );
};

const KpiCard = ({ icon, title, value, bgColor, textColor }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className={`text-3xl font-bold ${textColor}`}>{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${bgColor}`}>{icon}</div>
      </div>
    </CardContent>
  </Card>
);

// ── Página ────────────────────────────────────────────────────────────────────
const AlistamientosReportesPage = () => {
  const { idLavanderia } = useParams();
  const { toast } = useToast();

  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 29);

  // Filtros de consulta
  const [fechaDesde, setFechaDesde] = useState(thirtyDaysAgo.toISOString().split('T')[0]);
  const [fechaHasta, setFechaHasta] = useState(today.toISOString().split('T')[0]);
  const [isLoading, setIsLoading]   = useState(false);
  const [rawData, setRawData]       = useState([]);
  const [hasData, setHasData]       = useState(false);
  const [zonaFilter, setZonaFilter] = useState('Todas');

  // Configuración de turnos
  const [turnoMInicio, setTurnoMInicio] = useState('06:00');
  const [turnoMFin,    setTurnoMFin]    = useState('14:00');
  const [turnoTInicio, setTurnoTInicio] = useState('14:00');
  const [turnoTFin,    setTurnoTFin]    = useState('22:00');

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const procesar = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAlistamientosByLavanderia(idLavanderia, {
        query: {
          estado: { $nin: ['Pendiente', 'Eliminado'] },
          fechaCreacion: {
            $gte: new Date(fechaDesde + 'T00:00:00').toISOString(),
            $lte: new Date(fechaHasta + 'T23:59:59').toISOString(),
          },
        },
        page: 1,
        limit: 10000,
      });

      const data = response.data || [];
      if (data.length === 0) {
        toast({ title: 'Sin resultados', description: 'No hay alistamientos en el rango seleccionado' });
        setHasData(false);
        setRawData([]);
        setIsLoading(false);
        return;
      }
      setRawData(data);
      setHasData(true);
    } catch (error) {
      console.error(error);
      toast({ title: 'Error', description: 'No se pudieron cargar los datos', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [idLavanderia, fechaDesde, fechaHasta, toast]);

  // ── Datos filtrados por zona ──────────────────────────────────────────────
  const filteredData = useMemo(() => {
    if (zonaFilter === 'Todas') return rawData;
    return rawData.filter(a => a.zona === zonaFilter);
  }, [rawData, zonaFilter]);

  // ── KPIs ─────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const totalAlistamientos = filteredData.length;
    const totalPrendas       = filteredData.reduce((s, a) => s + (a.totalItems || 0), 0);
    const totalKilos         = filteredData.reduce((s, a) => s + (a.pesoTotal  || 0), 0) / 1000;
    const promKg             = totalAlistamientos > 0 ? totalKilos / totalAlistamientos : 0;
    return { totalAlistamientos, totalPrendas, totalKilos, promKg };
  }, [filteredData]);

  // ── Por día (filtrado) ────────────────────────────────────────────────────
  const dataPorDia = useMemo(() => {
    const map = {};
    filteredData.forEach(a => {
      const d   = new Date(a.fechaCreacion);
      const key = `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
      const ord = d.toISOString().split('T')[0];
      if (!map[key]) map[key] = { fecha: key, alistamientos: 0, prendas: 0, kilos: 0, order: ord };
      map[key].alistamientos += 1;
      map[key].prendas       += a.totalItems || 0;
      map[key].kilos          = parseFloat((map[key].kilos + (a.pesoTotal || 0) / 1000).toFixed(2));
    });
    return Object.values(map).sort((a, b) => a.order.localeCompare(b.order));
  }, [filteredData]);

  // ── Por día agrupado por zona (vista Todas) ───────────────────────────────
  const dataPorDiaZona = useMemo(() => {
    const map = {};
    rawData.forEach(a => {
      const d   = new Date(a.fechaCreacion);
      const key = `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
      const ord = d.toISOString().split('T')[0];
      if (!map[key]) map[key] = { fecha: key, order: ord, Lenceria: 0, Toallas: 0, LenceriaKg: 0, ToallasKg: 0 };
      const z = a.zona;
      if (z === 'Lenceria' || z === 'Toallas') {
        map[key][z]         += a.totalItems || 0;
        map[key][`${z}Kg`]  = parseFloat((map[key][`${z}Kg`] + (a.pesoTotal || 0) / 1000).toFixed(2));
      }
    });
    return Object.values(map).sort((a, b) => a.order.localeCompare(b.order));
  }, [rawData]);

  // ── Resumen por zona ──────────────────────────────────────────────────────
  const zonaResumen = useMemo(() =>
    ZONAS.map(zona => {
      const items = rawData.filter(a => a.zona === zona);
      return {
        zona,
        alistamientos: items.length,
        prendas: items.reduce((s, a) => s + (a.totalItems || 0), 0),
        kilos: parseFloat((items.reduce((s, a) => s + (a.pesoTotal || 0), 0) / 1000).toFixed(2)),
      };
    }),
  [rawData]);

  // ── Top 10 prendas ────────────────────────────────────────────────────────
  const dataPorPrenda = useMemo(() => {
    const map = {};
    filteredData.forEach(a => {
      (a.items || []).forEach(item => {
        const nombre = (item.nombre || 'Desconocido').trim();
        if (!map[nombre]) map[nombre] = { nombre, cantidad: 0, kilos: 0 };
        map[nombre].cantidad += item.cantidad || 0;
        map[nombre].kilos     = parseFloat((map[nombre].kilos + ((item.cantidad || 0) * (item.peso || 0)) / 1000).toFixed(2));
      });
    });
    return Object.values(map)
      .sort((a, b) => b.cantidad - a.cantidad)
      .slice(0, 10)
      .map(p => ({ ...p, nombre: p.nombre.length > 24 ? p.nombre.substring(0, 24) + '…' : p.nombre }));
  }, [filteredData]);

  // ── Tabla por cliente ─────────────────────────────────────────────────────
  const dataPorCliente = useMemo(() => {
    const map = {};
    filteredData.forEach(a => {
      const nombre = a.cliente?.nombre || 'Sin cliente';
      if (!map[nombre]) map[nombre] = { nombre, alistamientos: 0, prendas: 0, kilos: 0 };
      map[nombre].alistamientos += 1;
      map[nombre].prendas       += a.totalItems || 0;
      map[nombre].kilos          = parseFloat((map[nombre].kilos + (a.pesoTotal || 0) / 1000).toFixed(2));
    });
    return Object.values(map).sort((a, b) => b.prendas - a.prendas);
  }, [filteredData]);

  // ── Turnos (siempre sobre rawData para mostrar ambas zonas) ──────────────
  const dataTurnos = useMemo(() => {
    const mIni = toMin(turnoMInicio), mFin = toMin(turnoMFin);
    const tIni = toMin(turnoTInicio), tFin = toMin(turnoTFin);
    const blank = () => ({ prendas: 0, kilos: 0, alistamientos: 0 });
    const r = {
      Mañana: { Lenceria: blank(), Toallas: blank() },
      Tarde:  { Lenceria: blank(), Toallas: blank() },
      Otro:   { Lenceria: blank(), Toallas: blank() },
    };

    rawData.forEach(a => {
      const d    = new Date(a.fechaCreacion);
      const min  = d.getHours() * 60 + d.getMinutes();
      const zona = a.zona;
      if (zona !== 'Lenceria' && zona !== 'Toallas') return;

      const turno = (min >= mIni && min < mFin) ? 'Mañana'
                  : (min >= tIni && min < tFin) ? 'Tarde'
                  : 'Otro';

      r[turno][zona].alistamientos += 1;
      r[turno][zona].prendas       += a.totalItems || 0;
      r[turno][zona].kilos          = parseFloat((r[turno][zona].kilos + (a.pesoTotal || 0) / 1000).toFixed(2));
    });
    return r;
  }, [rawData, turnoMInicio, turnoMFin, turnoTInicio, turnoTFin]);

  // datos para gráfico de turnos (barra agrupada)
  const chartTurnos = useMemo(() =>
    ['Mañana', 'Tarde', 'Otro'].map(t => ({
      turno: `${TURNO_CONFIG[t].icon} ${t}`,
      'Lenceria (prendas)': dataTurnos[t].Lenceria.prendas,
      'Toallas (prendas)':  dataTurnos[t].Toallas.prendas,
      'Lenceria (kg)':      dataTurnos[t].Lenceria.kilos,
      'Toallas (kg)':       dataTurnos[t].Toallas.kilos,
    })),
  [dataTurnos]);

  // ── Exportar Excel ────────────────────────────────────────────────────────
  const exportarExcel = () => {
    const wb = XLSX.utils.book_new();
    const zonaLabel = zonaFilter === 'Todas' ? 'todas' : zonaFilter;

    XLSX.utils.book_append_sheet(wb,
      XLSX.utils.json_to_sheet(zonaResumen.map(z => ({
        Zona: z.zona, Alistamientos: z.alistamientos, Prendas: z.prendas, 'Kilos (kg)': z.kilos,
      }))),
      'Resumen por Zona'
    );
    XLSX.utils.book_append_sheet(wb,
      XLSX.utils.json_to_sheet(dataPorDia.map(d => ({
        Fecha: d.fecha, Alistamientos: d.alistamientos, Prendas: d.prendas, 'Kilos (kg)': d.kilos,
      }))),
      'Por Día'
    );
    XLSX.utils.book_append_sheet(wb,
      XLSX.utils.json_to_sheet(dataPorPrenda.map(p => ({
        Prenda: p.nombre, Cantidad: p.cantidad, 'Kilos (kg)': p.kilos,
      }))),
      'Por Prenda'
    );
    XLSX.utils.book_append_sheet(wb,
      XLSX.utils.json_to_sheet(dataPorCliente.map(c => ({
        Cliente: c.nombre, Alistamientos: c.alistamientos,
        Prendas: c.prendas, 'Kilos (kg)': c.kilos,
        'Prom. kg/alist.': parseFloat((c.kilos / c.alistamientos).toFixed(2)),
      }))),
      'Por Cliente'
    );
    // Hoja de turnos
    const turnosRows = [];
    ['Mañana', 'Tarde', 'Otro'].forEach(t => {
      ZONAS.forEach(z => {
        turnosRows.push({
          Turno: t,
          'Rango': t === 'Mañana' ? `${turnoMInicio} – ${turnoMFin}` : t === 'Tarde' ? `${turnoTInicio} – ${turnoTFin}` : 'Fuera de turno',
          Zona: z,
          Alistamientos: dataTurnos[t][z].alistamientos,
          Prendas: dataTurnos[t][z].prendas,
          'Kilos (kg)': dataTurnos[t][z].kilos,
        });
      });
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(turnosRows), 'Por Turno');

    XLSX.writeFile(wb, `reporte_alistamientos_${zonaLabel}_${fechaDesde}_${fechaHasta}.xlsx`);
  };

  const activeZonaColor = zonaFilter !== 'Todas' ? ZONA_COLORS[zonaFilter] : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-4 md:p-6 lg:p-8 space-y-5"
    >
      <div className="mb-4">
        <Button asChild variant="outline">
          <Link to={`/centros-lavado/${idLavanderia}/alistamientos`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver a Alistamientos
          </Link>
        </Button>
      </div>

      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-purple-500/10 p-3 rounded-lg">
                <BarChart2 className="h-8 w-8 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-2xl">Reportes de Alistamientos</CardTitle>
                <CardDescription>Análisis por período, zona, turno y cliente</CardDescription>
              </div>
            </div>
              {hasData && (
                <Button onClick={exportarExcel} className="bg-green-600 hover:bg-green-700 text-white gap-2">
                  <Download className="h-4 w-4" />
                  Exportar Excel
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-1">
                <Label htmlFor="fechaDesde">Desde</Label>
                <Input id="fechaDesde" type="date" value={fechaDesde}
                  onChange={e => setFechaDesde(e.target.value)} className="w-40" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="fechaHasta">Hasta</Label>
                <Input id="fechaHasta" type="date" value={fechaHasta}
                  onChange={e => setFechaHasta(e.target.value)} className="w-40" />
              </div>
              <Button onClick={procesar} disabled={isLoading} className="gap-2">
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart2 className="h-4 w-4" />}
                {isLoading ? 'Consultando...' : 'Consultar'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sin datos */}
        {!hasData && !isLoading && (
          <Card>
            <CardContent className="p-12 text-center">
              <BarChart2 className="h-14 w-14 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-400 text-base">
                Selecciona un rango de fechas y presiona <strong>Consultar</strong>
              </p>
            </CardContent>
          </Card>
        )}

        {hasData && (
          <>
            {/* Tabs de zona */}
            <div className="flex gap-2 flex-wrap">
              {['Todas', ...ZONAS].map(z => (
                <button
                  key={z}
                  onClick={() => setZonaFilter(z)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                    zonaFilter === z
                      ? z === 'Todas'    ? 'bg-gray-800 text-white border-transparent'
                      : z === 'Lenceria' ? 'bg-purple-600 text-white border-transparent'
                                         : 'bg-blue-600 text-white border-transparent'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {z}
                  {z !== 'Todas' && (
                    <span className="ml-2 text-xs opacity-75">
                      {rawData.filter(a => a.zona === z).length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Cards resumen por zona (vista Todas) */}
            {zonaFilter === 'Todas' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {zonaResumen.map(z => {
                  const c = ZONA_COLORS[z.zona];
                  return (
                    <Card key={z.zona} className={`border-l-4 ${c.border}`}>
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className={`font-semibold text-base ${c.text}`}>{z.zona}</h3>
                          <Badge className={c.badge}>{z.alistamientos} alistamientos</Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-center">
                          <div>
                            <p className={`text-2xl font-bold ${c.text}`}>{fmtNum(z.alistamientos)}</p>
                            <p className="text-xs text-gray-500 mt-0.5">Alistamientos</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-gray-700">{fmtNum(z.prendas)}</p>
                            <p className="text-xs text-gray-500 mt-0.5">Prendas</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-green-600">{z.kilos.toFixed(1)}</p>
                            <p className="text-xs text-gray-500 mt-0.5">Kilos</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard
                icon={<PackageCheck className="h-6 w-6 text-purple-600" />}
                title={`Alistamientos${zonaFilter !== 'Todas' ? ` — ${zonaFilter}` : ''}`}
                value={fmtNum(kpis.totalAlistamientos)}
                bgColor="bg-purple-100" textColor="text-purple-600"
              />
              <KpiCard
                icon={<Package className="h-6 w-6 text-blue-600" />}
                title="Total Prendas"
                value={fmtNum(kpis.totalPrendas)}
                bgColor="bg-blue-100" textColor="text-blue-600"
              />
              <KpiCard
                icon={<Scale className="h-6 w-6 text-green-600" />}
                title="Total Kilos"
                value={fmtKg(kpis.totalKilos)}
                bgColor="bg-green-100" textColor="text-green-600"
              />
              <KpiCard
                icon={<TrendingUp className="h-6 w-6 text-orange-600" />}
                title="Prom. kg / Alistamiento"
                value={fmtKg(kpis.promKg)}
                bgColor="bg-orange-100" textColor="text-orange-600"
              />
            </div>

            {/* Gráfico diario — prendas (+ kilos como línea en zona específica) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-purple-600" />
                  {zonaFilter === 'Todas'
                    ? 'Prendas por Día — Lenceria vs Toallas'
                    : `Actividad Diaria — ${zonaFilter}`}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={290}>
                  {zonaFilter === 'Todas' ? (
                    <BarChart data={dataPorDiaZona} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="Lenceria" name="Lenceria (prendas)" fill={ZONA_COLORS.Lenceria.bar} radius={[3,3,0,0]} />
                      <Bar dataKey="Toallas"  name="Toallas (prendas)"  fill={ZONA_COLORS.Toallas.bar}  radius={[3,3,0,0]} />
                    </BarChart>
                  ) : (
                    <ComposedChart data={dataPorDia} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left"  tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `${v}kg`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="prendas" name="Prendas"
                        fill={activeZonaColor?.bar ?? '#8b5cf6'} radius={[3,3,0,0]} />
                      <Line yAxisId="right" type="monotone" dataKey="kilos" name="Kilos"
                        stroke="#10b981" strokeWidth={2.5} dot={{ r: 3, fill: '#10b981' }} />
                    </ComposedChart>
                  )}
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* ── Kilos Totales por Día — ANCHO COMPLETO ── */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Scale className="h-5 w-5 text-green-600" />
                  {zonaFilter === 'Todas'
                    ? 'Kilos por Día — Lenceria vs Toallas'
                    : `Kilos por Día — ${zonaFilter}`}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={320}>
                  {zonaFilter === 'Todas' ? (
                    <BarChart data={dataPorDiaZona} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}kg`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="LenceriaKg" name="Lenceria (kg)" fill={ZONA_COLORS.Lenceria.bar} radius={[3,3,0,0]} />
                      <Bar dataKey="ToallasKg"  name="Toallas (kg)"  fill={ZONA_COLORS.Toallas.bar}  radius={[3,3,0,0]} />
                    </BarChart>
                  ) : (
                    <BarChart data={dataPorDia} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}kg`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="kilos" name="Kilos"
                        fill={activeZonaColor?.bar ?? '#10b981'} radius={[3,3,0,0]} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Top 10 Prendas — ancho completo */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  Top 10 Prendas por Cantidad
                  {zonaFilter !== 'Todas' && (
                    <Badge className={ZONA_COLORS[zonaFilter].badge}>{zonaFilter}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dataPorPrenda.length === 0 ? (
                  <p className="text-gray-400 text-sm py-8 text-center">Sin datos</p>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={dataPorPrenda} layout="vertical"
                      margin={{ top: 0, right: 40, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="nombre" type="category" tick={{ fontSize: 11 }} width={150} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="cantidad" name="Cantidad"
                        fill={activeZonaColor?.bar ?? '#3b82f6'} radius={[0,3,3,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* ════════════════════════════════════════════════════════════════
                SECCIÓN TURNOS
            ════════════════════════════════════════════════════════════════ */}
            <Card className="border-2 border-amber-200">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-600" />
                    Análisis por Turno — Lenceria y Toallas
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Configura los rangos horarios según tus turnos de trabajo
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">

                {/* Config rangos */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Mañana */}
                  <div className={`rounded-xl border p-4 ${TURNO_CONFIG.Mañana.bg} ${TURNO_CONFIG.Mañana.border}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">🌅</span>
                      <span className={`font-semibold ${TURNO_CONFIG.Mañana.color}`}>Turno Mañana</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="space-y-1 flex-1">
                        <Label className="text-xs text-gray-500">Inicio</Label>
                        <Input type="time" value={turnoMInicio}
                          onChange={e => setTurnoMInicio(e.target.value)}
                          className="bg-white" />
                      </div>
                      <span className="text-gray-400 mt-5">—</span>
                      <div className="space-y-1 flex-1">
                        <Label className="text-xs text-gray-500">Fin</Label>
                        <Input type="time" value={turnoMFin}
                          onChange={e => setTurnoMFin(e.target.value)}
                          className="bg-white" />
                      </div>
                    </div>
                  </div>

                  {/* Tarde */}
                  <div className={`rounded-xl border p-4 ${TURNO_CONFIG.Tarde.bg} ${TURNO_CONFIG.Tarde.border}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">🌆</span>
                      <span className={`font-semibold ${TURNO_CONFIG.Tarde.color}`}>Turno Tarde</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="space-y-1 flex-1">
                        <Label className="text-xs text-gray-500">Inicio</Label>
                        <Input type="time" value={turnoTInicio}
                          onChange={e => setTurnoTInicio(e.target.value)}
                          className="bg-white" />
                      </div>
                      <span className="text-gray-400 mt-5">—</span>
                      <div className="space-y-1 flex-1">
                        <Label className="text-xs text-gray-500">Fin</Label>
                        <Input type="time" value={turnoTFin}
                          onChange={e => setTurnoTFin(e.target.value)}
                          className="bg-white" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tabla resumen por turno */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Turno</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-600">Horario</th>
                        {/* Lenceria */}
                        <th className="text-right py-3 px-4 font-semibold text-purple-600">
                          Lenceria — Alist.
                        </th>
                        <th className="text-right py-3 px-4 font-semibold text-purple-600">
                          Lenceria — Prendas
                        </th>
                        <th className="text-right py-3 px-4 font-semibold text-purple-600">
                          Lenceria — Kilos
                        </th>
                        {/* Toallas */}
                        <th className="text-right py-3 px-4 font-semibold text-blue-600">
                          Toallas — Alist.
                        </th>
                        <th className="text-right py-3 px-4 font-semibold text-blue-600">
                          Toallas — Prendas
                        </th>
                        <th className="text-right py-3 px-4 font-semibold text-blue-600">
                          Toallas — Kilos
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {['Mañana', 'Tarde', 'Otro'].map(turno => {
                        const cfg = TURNO_CONFIG[turno];
                        const horario = turno === 'Mañana' ? `${turnoMInicio} – ${turnoMFin}`
                                      : turno === 'Tarde'  ? `${turnoTInicio} – ${turnoTFin}`
                                      : 'Fuera de turno';
                        const L = dataTurnos[turno].Lenceria;
                        const T = dataTurnos[turno].Toallas;
                        return (
                          <tr key={turno} className={`border-b transition-colors hover:bg-gray-50`}>
                            <td className="py-3 px-4">
                              <span className={`font-semibold ${cfg.color}`}>
                                {cfg.icon} {turno}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center text-xs text-gray-500">{horario}</td>
                            <td className="py-3 px-4 text-right text-purple-600">{L.alistamientos}</td>
                            <td className="py-3 px-4 text-right font-semibold text-purple-600">{fmtNum(L.prendas)}</td>
                            <td className="py-3 px-4 text-right font-semibold text-purple-600">{fmtKg(L.kilos)}</td>
                            <td className="py-3 px-4 text-right text-blue-600">{T.alistamientos}</td>
                            <td className="py-3 px-4 text-right font-semibold text-blue-600">{fmtNum(T.prendas)}</td>
                            <td className="py-3 px-4 text-right font-semibold text-blue-600">{fmtKg(T.kilos)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 font-semibold border-t-2">
                        <td colSpan={2} className="py-3 px-4">Total</td>
                        {/* Totales Lenceria */}
                        <td className="py-3 px-4 text-right text-purple-600">
                          {['Mañana','Tarde','Otro'].reduce((s, t) => s + dataTurnos[t].Lenceria.alistamientos, 0)}
                        </td>
                        <td className="py-3 px-4 text-right text-purple-600">
                          {fmtNum(['Mañana','Tarde','Otro'].reduce((s, t) => s + dataTurnos[t].Lenceria.prendas, 0))}
                        </td>
                        <td className="py-3 px-4 text-right text-purple-600">
                          {fmtKg(parseFloat(['Mañana','Tarde','Otro'].reduce((s, t) => s + dataTurnos[t].Lenceria.kilos, 0).toFixed(2)))}
                        </td>
                        {/* Totales Toallas */}
                        <td className="py-3 px-4 text-right text-blue-600">
                          {['Mañana','Tarde','Otro'].reduce((s, t) => s + dataTurnos[t].Toallas.alistamientos, 0)}
                        </td>
                        <td className="py-3 px-4 text-right text-blue-600">
                          {fmtNum(['Mañana','Tarde','Otro'].reduce((s, t) => s + dataTurnos[t].Toallas.prendas, 0))}
                        </td>
                        <td className="py-3 px-4 text-right text-blue-600">
                          {fmtKg(parseFloat(['Mañana','Tarde','Otro'].reduce((s, t) => s + dataTurnos[t].Toallas.kilos, 0).toFixed(2)))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Gráfico de turnos */}
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartTurnos} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="turno" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="Lenceria (prendas)" fill={ZONA_COLORS.Lenceria.bar} radius={[3,3,0,0]} />
                    <Bar dataKey="Toallas (prendas)"  fill={ZONA_COLORS.Toallas.bar}  radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>

              </CardContent>
            </Card>

            {/* Tabla por cliente */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  Resumen por Cliente ({dataPorCliente.length})
                  {zonaFilter !== 'Todas' && (
                    <Badge className={ZONA_COLORS[zonaFilter].badge}>{zonaFilter}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">#</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Cliente</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-600">Alistamientos</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-600">Prendas</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-600">Kilos</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-600">Prom. kg/alist.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dataPorCliente.map((c, idx) => (
                        <tr key={c.nombre} className="border-b hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4 text-gray-400">{idx + 1}</td>
                          <td className="py-3 px-4 font-medium">{c.nombre}</td>
                          <td className="py-3 px-4 text-right">{c.alistamientos}</td>
                          <td className="py-3 px-4 text-right font-semibold text-blue-600">{fmtNum(c.prendas)}</td>
                          <td className="py-3 px-4 text-right font-semibold text-green-600">{fmtKg(c.kilos)}</td>
                          <td className="py-3 px-4 text-right text-gray-500">
                            {(c.kilos / c.alistamientos).toFixed(1)} kg
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 font-semibold border-t-2">
                        <td colSpan={2} className="py-3 px-4">Total</td>
                        <td className="py-3 px-4 text-right">{kpis.totalAlistamientos}</td>
                        <td className="py-3 px-4 text-right text-blue-600">{fmtNum(kpis.totalPrendas)}</td>
                        <td className="py-3 px-4 text-right text-green-600">{fmtKg(kpis.totalKilos)}</td>
                        <td className="py-3 px-4 text-right text-gray-500">{kpis.promKg.toFixed(1)} kg</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </motion.div>
  );
};

export default AlistamientosReportesPage;
